/**
 * Hiring from a store's row on a table that is not the Stores page: Dashboard › Deployment ›
 * "Undeployed Stores" (product owner, 16 Oct 2026: "a separate Actions column there, where the
 * user would be able to link and open that particular store or add the respective option to
 * raise and hire on that respective store", built as Link an opening and Raise hiring; the
 * owner draws the "open that store" link, to the Stores page, itself, as it needs no hiring).
 *
 * The same windows, with the same props, the Stores page opens from its rows: Raise hiring
 * (`entry="store"`, the store fixed), Ask for hiring (the same window in ask mode), Link an
 * opening (the store fixed), the store's hiring window (HiringPanel) and, from that window, a
 * waiting hiring request's window. Nothing of what those windows do is changed here.
 *
 * WHO SEES WHAT, as on the Stores page, from the same server answers:
 *   - `shown`: the access answer says this person may see hiring in this project (the project's
 *     switch is on and they hold the Hiring tick), and the owner's table is on screen. Until that
 *     answer is in, on any failure of it, and while the table is not shown, false: the owner
 *     draws no column and nothing is asked.
 *   - `canRaise`: "Raise & cancel hiring" AND the hiring list says Raise is on and they may
 *     raise here. Not drawn until the list has answered.
 *   - `canAsk`: the list of hiring requests says they may send one, and they do not hold
 *     "Raise & cancel hiring". Never beside `canRaise`.
 *   - `canLink`: Hiring (manage).
 *   - `rowOf(store).status`: the store already has hiring followed, being raised or waiting for
 *     confirmation (the Stores page's Hiring column's word); null when it has none, or when the
 *     list has not been read. `openHover` says it, with the Stores page's Hiring dot's hover
 *     (a request waiting, or why it needs attention) where that has one.
 *   - `rowOf(store).links` and `.request`: what the store follows and the hiring request
 *     waiting for it, as the Stores page's Hiring column hands them to `StoreHiringDot`, and
 *     `listStatus`, the store-hiring read's own state (a word once it is `ready`, a dash when
 *     it `failed`, a placeholder while it loads). The owner draws its Hiring column from these:
 *     the same read as the Actions, never a second one (product owner, 16 Oct 2026: after
 *     linking an opening to a store, "user can open that opening and can see all details in
 *     it - and candidates and everything", where the store is).
 *   - `rowOf(store).raiseRefused`: why no hiring may be raised or asked for from this row now,
 *     greyed with this on hover, before anyone fills the form in: the store is not Active, or
 *     is marked Outside Approved – Redeploy (the server's own refusals, read off the store's
 *     raw fields, `storeRaiseRefusal`), or the owner's list shows a past month (`raiseHeld`).
 *     The store's hiring window then offers no Raise or Ask for it either.
 *
 * AFTER AN ACTION, exactly as on the Stores page. Every window here tells the hook what it did,
 * and the hook puts the server's answer into the list at once (`apply`) and reads the list
 * again behind it (`reload`): a link made (`onLinked`), a raise sent (`onChanged`), a link
 * stopped, cancelled or closed from the store's window (`onChanged`), a request confirmed
 * (`onLink`); an answer that could not be read, or a list found behind, reads it again
 * (`onOutOfDate`). A hiring request sent (`onAsked`) goes into the shared list of hiring
 * requests at once, from its answer. The row's Hiring word and its "Open hiring" follow on the
 * next render, with no reload of the page. Hiring linked or raised in ANOTHER tab (the row's
 * "Open … in Stores" opens one) shows when the person comes back to this one: both lists are
 * read again then (`useReadAgainOnReturn`).
 *
 * The owner writes the column's header and cells itself, with the shared table-actions
 * controls (the source check reads one file at a time and counts the cells of each row), and
 * puts `windows` once on the page, outside the table, so a re-read of the table never closes
 * a window that is open.
 *
 * A store on such a list can stand for several store rows that share one store code
 * (`storeIds`): what any of them follows or waits for counts for the row. Hiring is raised
 * and linked for `storeId`, the row the list shows.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { HiringDashStatus, HiringLink, HiringRequest } from '../../services/api';
import { STORE_STATUS, STORE_STATUS_REDEPLOY } from '../../constants/storeStatus';
import { HiringPanel } from './HiringPanel';
import { HiringRequestWindow } from './HiringRequestWindow';
import { LinkHiringModal } from './LinkHiringModal';
import { RaiseHiringModal, type RaiseHiringSubject } from './RaiseHiringModal';
import { RAISE_WORDS, REQUEST_WORDS, hasRaiseInFlight } from './hiringText';
import { DASH_STATUS_WORDS, storeHiringStatus, storeHiringWhy } from './hiringText.dashboard';
import { forgetHiringAccess, useHiringAccess } from './useHiringAccess';
import { forgetHiringRequests, useHiringRequests } from './useHiringRequests';
import { useReadAgainOnReturn } from './useReadAgainOnReturn';
import { NO_HIRING_LINKS, storeHiringKey, useStoreHiring, type StoreHiringStatus } from './useStoreHiring';

/** A store as a row hands it to the hiring windows: what the Raise window's store line shows. */
export interface HiringRowStore {
  storeId: string;
  /** Every store row this one stands for (rows sharing its store code), `storeId` among them. */
  storeIds?: readonly string[];
  storeName: string;
  storeCode: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  hasMapLocation: boolean;
  /** The Status the store shows (Active, Outside Approved – Redeploy, …). */
  status: string;
  staffingStatus: string;
  /** Why the server refuses any raise for this store today (`storeRaiseRefusal`); null or absent when it does not. */
  raiseRefused?: StoreRaiseRefusal | null;
}

/** The two refusals of a store the server makes before anything else of a raise, or of a request to raise. */
export type StoreRaiseRefusal = 'not_active' | 'redeploy';

/**
 * The server's own store check of a raise (`checkStoreStatus`, hiring-link-raise.logic.ts),
 * read off the store's RAW fields from GET /stores: any stored status but Active (a blank one
 * too, which the Status column shows as Active) is refused, and so is a store marked
 * Outside Approved – Redeploy (stored Active, with `redeploy_pending`).
 */
export const storeRaiseRefusal = (
  store: { store_status?: string | null; redeploy_pending?: boolean | null },
): StoreRaiseRefusal | null => {
  if (String(store.store_status ?? '').trim().toLowerCase() !== STORE_STATUS.ACTIVE.toLowerCase()) return 'not_active';
  if (store.redeploy_pending === true) return 'redeploy';
  return null;
};

/** The words of the controls that only these rows have. Raise and Ask keep the Stores page's. */
export const STORE_ROW_HIRING_WORDS = {
  header: 'Actions',
  raise: RAISE_WORDS.rowControl,
  raiseBusy: RAISE_WORDS.rowControlBusy,
  ask: REQUEST_WORDS.rowControl,
  askWaiting: REQUEST_WORDS.rowControlWaiting,
  link: (storeName: string): string => `Link an opening to ${storeName}`,
  open: (storeName: string): string => `Open hiring for ${storeName}`,
  /** The status word, then the Stores page's Hiring dot's hover where it has one. */
  openHover: (status: HiringDashStatus, why?: string | null): string =>
    `Hiring for this store: ${DASH_STATUS_WORDS[status]}${why ? `. ${why}` : ''}`,
  /** The server's own sentences for a store it refuses, on the greyed Raise or Ask. */
  refused: {
    not_active: `This store is not ${STORE_STATUS.ACTIVE.toLowerCase()}, so hiring cannot be raised for it`,
    redeploy: `This store is marked ${STORE_STATUS_REDEPLOY}, so hiring cannot be raised for it`,
  } satisfies Record<StoreRaiseRefusal, string>,
  /** A list showing a past month: its rows are stores as they stood then, and hiring is for the store as it is today. */
  pastMonth: (day: string): string => `This list shows stores as they stood on ${day}. Choose the current month to raise or ask for hiring`,
} as const;

/**
 * "Open hiring for …" on a store's row, the same on the Stores page and on Undeployed Stores:
 * the store's word, and the control's hover (the word, then the Hiring dot's own hover where it
 * has one), from what the store follows and the hiring request waiting for it. A null status:
 * nothing is followed, being raised or waiting, and the row offers no "Open hiring".
 */
export const storeOpenHiringOf = (
  links: readonly HiringLink[],
  request: HiringRequest | null,
): { status: HiringDashStatus | null; openHover: string | null } => {
  const status = storeHiringStatus(links, request);
  return { status, openHover: status ? STORE_ROW_HIRING_WORDS.openHover(status, storeHiringWhy(links, request, status)) : null };
};

/** What one row may offer, read off the two lists as they are now. */
export interface StoreRowHiringState {
  /** The most pressing of the store's hiring (followed, being raised, or a request waiting); null when none. */
  status: HiringDashStatus | null;
  /** The hover of "Open hiring": the status word, and the Stores page's Hiring dot's hover. Null with `status`. */
  openHover: string | null;
  /** What the store follows (every row of its code), for its Hiring cell. Empty until the list is read. */
  links: readonly HiringLink[];
  /** The hiring request waiting for the store, for those shown requests; null when none or not shown. */
  request: HiringRequest | null;
  /** A request to raise hiring for the store has not ended: no second one is offered. */
  raiseBusy: boolean;
  /** A hiring request for the store waits for confirmation: no second one is asked for. */
  askWaiting: boolean;
  /** Why no hiring may be raised or asked for from this row now (Raise and Ask greyed, this on hover); null when it may. */
  raiseRefused: string | null;
}

export interface StoreRowHiring {
  shown: boolean;
  /** The store-hiring read's state, for the owner's Hiring column: 'off' while `shown` is false. */
  listStatus: StoreHiringStatus;
  canRaise: boolean;
  canAsk: boolean;
  canLink: boolean;
  rowOf: (store: HiringRowStore) => StoreRowHiringState;
  openRaise: (store: HiringRowStore) => void;
  openAsk: (store: HiringRowStore) => void;
  openLink: (store: HiringRowStore) => void;
  openHiring: (store: HiringRowStore) => void;
  /** Every window, for the owner to put once on the page. */
  windows: ReactNode;
}

type LinkPick = { budgetId: number; openingId: number };

type OpenWindow =
  // `replacing`: opened by "Raise again" (or "Ask for hiring") on an opening deleted in Recruitment.
  | { kind: 'panel' | 'raise' | 'ask'; store: HiringRowStore; replacing?: HiringLink }
  | { kind: 'link'; store: HiringRowStore; pick: LinkPick | null }
  | { kind: 'request'; request: HiringRequest };

const NO_REQUESTS: readonly HiringRequest[] = [];

/** The store's own row first, then the rows that share its code; each once. */
const keysOf = (store: HiringRowStore): string[] =>
  [...new Set([store.storeId, ...(store.storeIds ?? [])].map(storeHiringKey))];

/** The windows' own view of the store. */
const raiseSubjectOf = (store: HiringRowStore): RaiseHiringSubject => ({
  kind: 'store',
  storeId: store.storeId,
  storeName: store.storeName,
  storeCode: store.storeCode,
  address: store.address,
  city: store.city,
  state: store.state,
  pincode: store.pincode,
  hasMapLocation: store.hasMapLocation,
  status: store.status,
  staffingStatus: store.staffingStatus,
});

export const useStoreRowHiring = ({
  projectId, userKey, tableShown, stores, onPlaced, raiseHeld = null,
}: {
  projectId: number | null | undefined;
  userKey: string;
  /** The owner's table is on screen: nothing of hiring is asked for while it is not. */
  tableShown: boolean;
  /** Why no row of the owner's list may raise or ask for hiring now (it shows a past month); null when they may. */
  raiseHeld?: string | null;
  /** The stores the table lists now: a request in the way under a budget opens its store's hiring when it is one of them. */
  stores: readonly HiringRowStore[];
  /** A hiring was closed as filled from the store's hiring window: someone was placed, and the owner's list may be behind. */
  onPlaced?: () => void;
}): StoreRowHiring => {
  const access = useHiringAccess(projectId, userKey);
  const shown = access.canView && tableShown && !!projectId;
  const storeHiring = useStoreHiring(projectId, shown);
  const hiringRequests = useHiringRequests(projectId, userKey, shown && access.canSeeRequests);
  const requestsShown = shown && hiringRequests.status === 'ready';
  // Raising was switched off under a window: its control goes, for this project, until the page is opened again.
  const [raiseGoneFor, setRaiseGoneFor] = useState<number | null>(null);
  const canRaise = shown && access.canRaise && storeHiring.raise.canRaise && raiseGoneFor !== projectId;
  const canAsk = requestsShown && hiringRequests.can.send && !access.canRaise;
  const canLink = shown && access.canManage;

  const [open, setOpen] = useState<OpenWindow | null>(null);
  // A window opened for one project does not follow the page to another, not even for one render.
  const [openFor, setOpenFor] = useState<number | null>(null);
  const projectNow = useRef(projectId);
  projectNow.current = projectId;
  // Hiring went off for the project, the table left the screen, or the page moved to another
  // project: every window closes with it, and nothing is said.
  useEffect(() => {
    setOpen(null);
  }, [shown, projectId]);
  const show = useCallback((next: OpenWindow | null) => {
    setOpen(next);
    setOpenFor(projectNow.current ?? null);
  }, []);

  const { byStore } = storeHiring;
  const linksOf = useCallback((store: HiringRowStore): readonly HiringLink[] => {
    const lists = keysOf(store).map((key) => byStore.get(key)).filter((l): l is HiringLink[] => !!l && l.length > 0);
    if (lists.length === 0) return NO_HIRING_LINKS;
    return lists.length === 1 ? lists[0] : lists.flat();
  }, [byStore]);
  const requestByStore = hiringRequests.byStore;
  const requestOf = useCallback((store: HiringRowStore): HiringRequest | null => {
    if (!requestsShown) return null;
    for (const key of keysOf(store)) {
      const request = requestByStore.get(key);
      if (request) return request;
    }
    return null;
  }, [requestsShown, requestByStore]);

  const listRead = storeHiring.status === 'ready';
  // The store's own refusal first: it holds whichever month is shown.
  const refusedOf = useCallback(
    (store: HiringRowStore): string | null => (store.raiseRefused ? STORE_ROW_HIRING_WORDS.refused[store.raiseRefused] : raiseHeld || null),
    [raiseHeld],
  );
  const rowOf = useCallback((store: HiringRowStore): StoreRowHiringState => {
    const links = linksOf(store);
    const request = requestOf(store);
    // Nothing is said of a store before the list is read: no word, and no "Open hiring".
    const { status, openHover } = listRead ? storeOpenHiringOf(links, request) : { status: null, openHover: null };
    return {
      status,
      openHover,
      links,
      request,
      raiseBusy: hasRaiseInFlight(links),
      askWaiting: !!request,
      raiseRefused: refusedOf(store),
    };
  }, [linksOf, requestOf, listRead, refusedOf]);

  const reloadStoreHiring = storeHiring.reload;
  const reloadRequests = hiringRequests.reload;
  // Something changed that either list may not show yet: both are read again.
  const reloadLists = useCallback(() => {
    reloadStoreHiring();
    reloadRequests();
  }, [reloadStoreHiring, reloadRequests]);
  // Hiring linked or raised in another tab (the row's "Open … in Stores" opens one): read again on return.
  useReadAgainOnReturn(shown, reloadLists);
  const hiringGone = useCallback(() => forgetHiringAccess(projectId), [projectId]);
  const raiseGone = useCallback(() => {
    setRaiseGoneFor(projectId ?? null);
    forgetHiringRequests(projectId);
    setOpen(null);
    reloadStoreHiring();
  }, [projectId, reloadStoreHiring]);

  const current = open && projectId && openFor === projectId ? open : null;
  const store = current && current.kind !== 'request' ? current.store : null;
  const storeLinks = store ? linksOf(store) : NO_HIRING_LINKS;
  const storeRequest = store ? requestOf(store) : null;

  let windows: ReactNode = null;
  if (current && projectId) {
    if (current.kind === 'panel' && shown) {
      const panelStore = current.store;
      // Greyed on the row, so not offered here either.
      const panelRefused = !!refusedOf(panelStore);
      windows = (
        <HiringPanel
          projectId={projectId}
          subject={{ kind: 'store', storeName: panelStore.storeName, storeCode: panelStore.storeCode, storeId: panelStore.storeId }}
          // Read off the list as it is NOW, so a link made or stopped shows without reopening.
          links={storeLinks}
          canManage={access.canManage}
          // Offered only to a holder of "Raise & cancel hiring" whom the list answer says may cancel here.
          canCancel={access.canRaise && storeHiring.cancel.canCancel}
          onClose={() => show(null)}
          // A stopped link leaves at once; both lists are then read again behind it. A hiring
          // closed as filled placed someone at this store: the owner reads its list again, for
          // the project the window was opened in only.
          onChanged={(stopped) => {
            if (stopped) storeHiring.apply(stopped);
            reloadLists();
            if (stopped?.closedAs === 'filled' && projectNow.current === projectId) onPlaced?.();
          }}
          onLinkHiring={access.canManage ? () => show({ kind: 'link', store: panelStore, pick: null }) : undefined}
          // Offered only to someone the server says may raise hiring here, for a store it would not refuse.
          onRaiseHiring={canRaise && !panelRefused ? () => show({ kind: 'raise', store: panelStore }) : undefined}
          onHiringGone={hiringGone}
          // The store's hiring request that waits for confirmation, drawn above its openings.
          requests={storeRequest ? [storeRequest] : NO_REQUESTS}
          onOpenRequest={(request) => show({ kind: 'request', request })}
          // Offered only to someone who may ask and may not raise, for a store the server would not refuse.
          onAskHiring={canAsk && !panelRefused ? () => show({ kind: 'ask', store: panelStore }) : undefined}
          // An opening deleted in Recruitment: the same two windows, handed the deleted link.
          onRaiseReplacing={canRaise && !panelRefused ? (deleted) => show({ kind: 'raise', store: panelStore, replacing: deleted }) : undefined}
          onAskReplacing={canAsk && !panelRefused ? (deleted) => show({ kind: 'ask', store: panelStore, replacing: deleted }) : undefined}
        />
      );
    } else if (current.kind === 'ask' && canAsk) {
      windows = (
        <RaiseHiringModal
          mode="ask"
          entry="store"
          projectId={projectId}
          subject={raiseSubjectOf(current.store)}
          // A raise already in flight for the store is shown in place of a form.
          links={storeLinks}
          // Asked for from a deleted opening: the result says it stays followed.
          replacingDeleted={current.replacing}
          onClose={() => show(null)}
          onChanged={(link) => {
            storeHiring.apply(link);
            storeHiring.reload();
          }}
          // The hiring request shows on its row at once, from the answer itself. The window
          // stays open: it is showing that it was sent for confirmation.
          onAsked={(request) => hiringRequests.apply(request)}
          onOutOfDate={reloadLists}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGone}
        />
      );
    } else if (current.kind === 'raise' && shown && access.canRaise) {
      const raiseStore = current.store;
      windows = (
        <RaiseHiringModal
          entry="store"
          projectId={projectId}
          subject={raiseSubjectOf(raiseStore)}
          // A request already being raised for the store is shown in place of a form.
          links={storeLinks}
          // Raised again from a deleted opening: once this raise made an opening, that one is no longer followed.
          replacingDeleted={current.replacing}
          onClose={() => show(null)}
          // The request shows on its row at once, from the answer itself; both lists are then
          // read again behind it. The window stays open: it is showing the result.
          onChanged={(link) => {
            storeHiring.apply(link);
            reloadLists();
          }}
          onOutOfDate={reloadLists}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGone}
          // The request in the way under a budget, when it is a store's on this list.
          openRequest={(linkId) => {
            for (const [key, links] of byStore) {
              if (!links.some((l) => l.id === linkId)) continue;
              const listed = stores.find((s) => keysOf(s).includes(key));
              return listed ? () => show({ kind: 'panel', store: listed }) : null;
            }
            return null;
          }}
          // Recruitment already has an unlinked opening with the store's name on it: this
          // window closes, and Link an opening opens for the same store on that opening.
          onLinkInstead={access.canManage ? ({ budgetId, openingId }) => show({ kind: 'link', store: raiseStore, pick: { budgetId, openingId } }) : undefined}
        />
      );
    } else if (current.kind === 'link' && access.canManage && shown) {
      windows = (
        <LinkHiringModal
          projectId={projectId}
          subject={{ kind: 'store', storeId: current.store.storeId, storeName: current.store.storeName, storeCode: current.store.storeCode }}
          // What the store already follows is not offered to it a second time.
          followed={storeLinks}
          initial={current.pick ?? undefined}
          onClose={() => show(null)}
          // The new link shows on its row at once, from the answer to the link itself; the
          // whole list is then read again behind it.
          onLinked={(link) => {
            show(null);
            if (link) storeHiring.apply(link);
            storeHiring.reload();
          }}
          onOutOfDate={storeHiring.reload}
          onHiringGone={hiringGone}
        />
      );
    } else if (current.kind === 'request' && shown && access.canSeeRequests) {
      windows = (
        <HiringRequestWindow
          projectId={projectId}
          // As the list has it now; once it has left the list, as it was opened.
          request={hiringRequests.requests.find((r) => r.id === current.request.id) ?? current.request}
          can={hiringRequests.can}
          onClose={() => show(null)}
          onRequest={(request) => hiringRequests.apply(request)}
          // A confirm made a link: it shows at once, and both lists are read again behind it.
          onLink={(link) => {
            storeHiring.apply(link);
            reloadLists();
          }}
          onOutOfDate={reloadLists}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGone}
        />
      );
    }
  }

  const openRaise = useCallback((s: HiringRowStore) => show({ kind: 'raise', store: s }), [show]);
  const openAsk = useCallback((s: HiringRowStore) => show({ kind: 'ask', store: s }), [show]);
  const openLink = useCallback((s: HiringRowStore) => show({ kind: 'link', store: s, pick: null }), [show]);
  const openHiring = useCallback((s: HiringRowStore) => show({ kind: 'panel', store: s }), [show]);

  return { shown, listStatus: storeHiring.status, canRaise, canAsk, canLink, rowOf, openRaise, openAsk, openLink, openHiring, windows };
};

export default useStoreRowHiring;
